import React, { useState, useEffect } from "react";
import "../../styles/serviceCallStyles/ServiceCall.css";
import { useNavigate } from "react-router-dom";
import Header from "../../components/HeaderFooter/Header";
import Footer from "../../components/HeaderFooter/Footer";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { getServiceCalls } from "../../services/API";
import { format } from "date-fns";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

const ServiceCall = () => {
  dayjs.extend(utc);

  const [activeTab, setActiveTab] = useState("All");
  const [selectedDate, setSelectedDate] = useState([null, null]);
  const [dropdownState, setDropdownState] = useState({
    serviceOpen: false,
    tasksOpen: false,
    teamOpen: false,
    dateOpen: false,
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [serviceData, setServiceData] = useState([]);
  const [taskOptions, setTaskOptions] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);  

  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const rowsPerPage = 7;
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getServiceCalls();
        console.log("API Response:", response.data);

        const formattedData = response.data.map((item) => {
          const employeesAssigned = item.task?.employeesAssigned || []; // Safely get employeesAssigned
          const employeeNames = employeesAssigned.map((emp) => emp.employeeName); // Extract employee names
        
          const startDate = employeesAssigned[0]?.timeStart || null; // Use existing employee data for start date
          const endDate = employeesAssigned[0]?.timeFinish || null; // Use existing employee data for end date
        
          const formattedStartDate = startDate
            ? dayjs.utc(startDate).format("YYYY-MM-DD")
            : "N/A";
          const formattedEndDate = endDate
            ? dayjs.utc(endDate).format("YYYY-MM-DD")
            : "N/A";
        
          const formattedTime = startDate
            ? format(new Date(startDate), "hh:mm a")
            : "N/A";

          const displayStartDate = startDate
          ? dayjs.utc(startDate).format("DD MMM ")
          : "N/A";

          const displayEndDate = endDate
          ? dayjs.utc(endDate).format("DD MMM YYYY")
          : "N/A";

          console.log("start display ", displayStartDate, " display end date ", displayEndDate);

          return {
            serviceCallId: item.serviceData.serviceCallId,
            serviceCallName: item.serviceData.serviceCallDescription,
            address: item.serviceData.address,
            date: `${formattedStartDate} - ${formattedEndDate}`,
            time: formattedTime,
            task: item.task?.taskName || "No Task Assigned",
            type: item.serviceData.siteStatus,
            employees: employeeNames,
            displayDateRange: `${displayStartDate} - ${displayEndDate}`
          };
        });
        setServiceData(formattedData);

        // Extract unique tasks
        const uniqueTasks = [
          ...new Set(formattedData.map((service) => service.task)),
        ];
        setTaskOptions(uniqueTasks); // Store unique tasks

        const uniqueEmployees = [
          ...new Set(
            response.data.flatMap((serviceCall) =>
              serviceCall.task.employeesAssigned.map((emp) => emp.employeeName)
            )
          ),
        ];
        setEmployeeOptions(uniqueEmployees); 

      } catch (error) {
        console.error("Error fetching service calls:", error);
      }
    };
    fetchData();
  }, []);

  const handleNewProjectClick = () => {
    const sd = Boolean(localStorage.getItem("serviceId"));
    if (sd) {
      localStorage.removeItem("serviceData");
      localStorage.removeItem("selectedTasks");
      localStorage.removeItem("dateRanges");
      localStorage.removeItem("uploadedFiles");
      localStorage.removeItem("serviceId");
    }
    navigate("/newservicecall");
  };

  const handleServiceCallClick = (id) => {
    navigate(`/servicedetails/${id}`);
  };

  const toggleDropdown = (dropdownName) => {
    setDropdownState((prevState) => ({
      ...prevState,
      serviceOpen: false,
      tasksOpen: false,
      teamOpen: false,
      dateOpen: false,
      [dropdownName]: !prevState[dropdownName],
    }));
  };

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  
  const filteredServiceData = serviceData.filter((item) => {
    const matchesSearch = item.address.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTab =
      activeTab === "All" ||
      (activeTab === "Onsite" && item.type.toLowerCase() === "onsite") ||
      (activeTab === "Offsite" && item.type.toLowerCase() === "offsite");

      const matchesEmployee = !selectedEmployee || item.employees.some(
          (emp) => emp.toLowerCase() === selectedEmployee.toLowerCase()
      ); 
       
    const matchesTask = !selectedTask || item.task === selectedTask; // Filter by selected task
    
    // Date range filtering
    const serviceStartDate = dayjs(item.date.split(" - ")[0]); 
    const serviceEndDate = dayjs(item.date.split(" - ")[1]); 
    
    const matchesDateRange =
      (!selectedDate[0] || serviceEndDate.isAfter(dayjs(selectedDate[0]).subtract(1, "day"))) &&
      (!selectedDate[1] || serviceStartDate.isBefore(dayjs(selectedDate[1]).add(1, "day")));

    return matchesSearch && matchesTab && matchesTask && matchesEmployee && matchesDateRange;
  });

  const totalPages = Math.ceil(filteredServiceData.length / rowsPerPage);
  const currentRows = filteredServiceData.slice(
    indexOfFirstRow,
    indexOfLastRow
  );

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    for (let i = 1; i <= totalPages; i++) {
      if (
        i === 1 ||
        i === totalPages ||
        (i >= currentPage - 1 && i <= currentPage + 1)
      ) {
        pageNumbers.push(
          <button
            key={i}
            onClick={() => handlePageChange(i)}
            className={`page-number ${currentPage === i ? "active" : ""}`}
          >
            {i}
          </button>
        );
      } else if (
        (i === currentPage - 2 && currentPage > 3) ||
        (i === currentPage + 2 && currentPage < totalPages - 2)
      ) {
        pageNumbers.push(
          <span key={i} className="dots">
            ...
          </span>
        );
      }
    }
    return pageNumbers;
  };

  return (
    <div className="service-container">
      <Header />
      <div className="content">
        <div className="tabs-container">
          <h2 className="tabs-title">Service Call</h2>
          <div className="tabs">
            <button
              className={`tab-item ${activeTab === "All" ? "active" : ""}`}
              onClick={() => setActiveTab("All")}
            >
              All
            </button>
            <button
              className={`tab-item ${activeTab === "Onsite" ? "active" : ""}`}
              onClick={() => setActiveTab("Onsite")}
            >
              Onsite
            </button>
            <button
              className={`tab-item ${activeTab === "Offsite" ? "active" : ""}`}
              onClick={() => setActiveTab("Offsite")}
            >
              Offsite
            </button>
          </div>
        </div>
      </div>

      <div className="serviceScreen">
        <div className="filter-container">
          <div className="filter-title">Filters</div>
          <div className="filter-section">
            <button
              onClick={() => toggleDropdown("serviceOpen")}
              className={`filter-btn ${
                dropdownState.serviceOpen ? "open" : ""
              }`}
            >
              Service Call Name
            </button>
            {dropdownState.serviceOpen && (
              <div className="filter-dropdown"></div>
            )}
          </div>

          <div className="filter-section">
            <button
              onClick={() => toggleDropdown("tasksOpen")}
              className={`filter-btn ${dropdownState.tasksOpen ? "open" : ""}`}
            >
              Tasks
            </button>

            {dropdownState.tasksOpen && (
              <div className="filter-dropdown">
                
                  {taskOptions.map((task, index) => (
                    <span
                      key={index}
                      className={selectedTask === task ? "selected-task" : "unselected-task"}
                      onClick={() =>
                        setSelectedTask(selectedTask === task ? null : task)
                      }
                    >
                      {task}
                    </span>
                  ))}
              
              </div>
            )}

          </div>
          
          <div className="filter-section">
            <button onClick={() => toggleDropdown("teamOpen")} className={`filter-btn ${dropdownState.teamOpen ? "open" : ""}`}>
              Team
            </button>

            {dropdownState.teamOpen && (
              <div className="filter-dropdown">
              
                  {employeeOptions.map((employee, index) => (
                    <span key={index} className={selectedEmployee === employee ? "selected-employee" : "unselected-employee"}
                      onClick={() => setSelectedEmployee( selectedEmployee === employee ? null : employee) }
                    >
                      {employee}
                    </span>
                  ))}
             
              </div>
            )}

          </div>

          
          <div className="filter-section">
            <button onClick={() => toggleDropdown("dateOpen")} className={`filter-btn ${dropdownState.dateOpen ? "open" : ""}`}>
              Date
            </button>
            {dropdownState.dateOpen && (
              <>
                <DatePicker selected={selectedDate[0]}
                  onChange={(update) => {
                    if (Array.isArray(update) && update.length === 2) { setSelectedDate(update); }
                  }}
                  dateFormat="dd/MM/yyyy" inline selectsRange startDate={selectedDate[0]} endDate={selectedDate[1]}
                  className="projectFilterCalender"  calendarClassName="custom-calendar" />

                  <button className="clear-date-btn" onClick={() => setSelectedDate([null, null])} >
                    Clear Date
                  </button>
              </>
            )}
          </div>
        </div>

        <div className="serv-task-header">
          <div className="search-container">
            <input
              type="text"
              placeholder="Enter ServiceCall Name to Search..."
              className="search-bar"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />

            <button className="new-service-btn" onClick={handleNewProjectClick}>
              <img src="/images/add.png" alt="icon" className="btn-icon" /> New
              Service Call
            </button>
          </div>

          <table className="serv-task-table">
            <thead>
              <tr>
                <th>
                  <img
                    src="/images/servicecall.svg"
                    alt="Service Call Icon"
                    className="label-icon"
                  />{" "}
                  Service Call Name
                </th>
                <th>
                  <img
                    src="/images/date.png"
                    alt="Date Icon"
                    className="label-icon"
                  />{" "}
                  Date
                </th>
                <th>
                  <img
                    src="/images/time.png"
                    alt="Time Icon"
                    className="label-icon"
                  />{" "}
                  Time
                </th>
                <th>
                  <img
                    src="/images/worker.png"
                    alt="Tasks Icon"
                    className="label-icon"
                  />{" "}
                  Tasks
                </th>
                <th>
                  <img
                    src="/images/type.png"
                    alt="Type Icon"
                    className="label-icon"
                  />{" "}
                  Type
                </th>
                <th></th>
              </tr>
            </thead>

            <tbody>
              {currentRows.length === 0 ? (
                <tr>
                  <td
                    colSpan="6"
                    style={{ textAlign: "center" }}
                    className="no-results"
                  >
                    No service calls found
                  </td>
                </tr>
              ) : (
                currentRows.map((item, index) => (
                  <tr key={index}>
                    <td>
                      <b>{item.address}</b>
                    </td>
                    <td>{item.displayDateRange}</td>
                    <td>{item.time}</td>
                    <td>{item.task}</td>
                    <td>{item.type}</td>
                    <td>
                      <button
                        onClick={() =>
                          handleServiceCallClick(item.serviceCallId)
                        }
                      >
                        <img
                          src="/images/arrow.png"
                          alt="Next Icon"
                          className="next-icon"
                        />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="servicepagination">
        {renderPageNumbers()}
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="pagination-next"
        >
          Next
        </button>
      </div>
      <Footer />
    </div>
  );
};

export default ServiceCall;
